/** Demandes de ramassage (marketplace NauvaTek + lien direct commerce). */

import { createI18nLabelMap } from '../utils/i18n/getStatusLabel';

export type PickupRequestStatus =
  | 'pending'
  | 'accepted'
  | 'scheduled'
  | 'picked_up'
  | 'in_progress'
  | 'ready_for_collection'
  | 'completed'
  | 'cancelled'
  | 'rejected';

export type PickupRequestSource =
  | 'nauvatek_marketplace'
  | 'merchant_direct_link'
  /** Dépôt comptoir : dossier créé depuis la caisse après vente. */
  | 'counter_dropoff';

export const PICKUP_REQUEST_STATUS_LABELS: Record<PickupRequestStatus, string> = createI18nLabelMap(
  'pickupRequestStatus',
  [
    'pending',
    'accepted',
    'scheduled',
    'picked_up',
    'in_progress',
    'ready_for_collection',
    'completed',
    'cancelled',
    'rejected',
  ] as const
);

/** Statuts exposés au suivi public (libellés client — jamais le motif de refus interne). */
export const PICKUP_PUBLIC_STATUS_LABELS: Record<PickupRequestStatus, string> = createI18nLabelMap(
  'pickupPublicStatus',
  [
    'pending',
    'accepted',
    'scheduled',
    'picked_up',
    'in_progress',
    'ready_for_collection',
    'completed',
    'cancelled',
    'rejected',
  ] as const
);

export function getPickupPublicStatusLabel(status: PickupRequestStatus): string {
  return PICKUP_PUBLIC_STATUS_LABELS[status] || status;
}

export interface PickupServiceArea {
  city: string;
  districts: string[];
}

export interface PickupBusinessConfig {
  /** Visible sur le hub marketplace `/pressing/ramassage`. */
  marketplaceEnabled: boolean;
  /** Slug unique pour `/pressing/ramassage/p/:slug`. */
  publicSlug: string;
  serviceAreas: PickupServiceArea[];
  /** Service catalogue forfaitaire pour les frais de ramassage. */
  feeServiceId?: string;
}

export interface PickupRequest {
  id: string;
  businessId: string;
  trackingId: string;
  source: PickupRequestSource;
  status: PickupRequestStatus;

  name: string;
  phone: string;
  email?: string;
  city: string;
  district?: string;
  address: string;
  requestedDate?: string;
  requestedTimeSlot?: string;
  message?: string;

  /** Date de passage planifié (YYYY-MM-DD). */
  scheduledDate?: string;
  /** Heure de passage planifié (HH:mm). */
  scheduledTime?: string;
  /** RDV calendrier lié à la planification. */
  appointmentId?: string;

  clientId?: string | null;
  assignedEmployeeId?: string;
  assignedEmployeeName?: string;
  siteId?: string;
  siteName?: string;

  transactionGroupId?: string;
  paymentPlanId?: string;
  prestationIds?: string[];

  /** Snapshot dénormalisé pour le suivi public. */
  publicSnapshot: {
    businessPublicName: string;
    statusLabel: string;
    updatedAt: Date;
  };

  createdAt: Date;
  updatedAt: Date;
  processedAt?: Date;
  processedBy?: string;
  rejectionReason?: string;
}

export interface PickupPublicTrackingView {
  trackingId: string;
  status: PickupRequestStatus;
  statusLabel: string;
  businessPublicName: string;
  /** Logo branding du pressing (co-branding suivi public). */
  businessLogo?: string | null;
  city: string;
  district?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePickupRequestInput {
  name: string;
  phone: string;
  email?: string;
  city: string;
  district?: string;
  address: string;
  requestedDate?: string;
  requestedTimeSlot?: string;
  message?: string;
  /** Marketplace : business choisi. */
  businessId?: string;
  /** Lien direct : slug commerce. */
  slug?: string;
}

/** Normalise un slug public (a-z, 0-9, tirets). */
export function normalizePickupSlug(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
}

export function isValidPickupSlug(slug: string): boolean {
  return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug) && slug.length >= 2 && slug.length <= 48;
}
